import { Apartment } from "@boark/shared/types/apartment";
import { ApartmentWithHoldingsAndLeasesAndSublets } from "@boark/shared/types/apartment-with-holdings-and-leases-and-sublets";
import { HoldingOwner } from "@boark/shared/types/holding-owner";
import { Tenant } from "@boark/shared/types/tenant";
import { getResidencyWithinDate } from "@boark/shared/utils/apartment-residency";
import { useSearchParams } from "react-router-dom";

import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetApartments } from "../../queries/get-apartments";
import { useLocale } from "../../stores/locale";
import { getAgeFromPersonalIdentificationNumber } from "../../utils/age";
import {
  compareContractLength,
  formatContractLength,
} from "../../utils/contract-length";
import { formatDate } from "../../utils/date";
import { formatPercentage } from "../../utils/format-percentage";
import { recordIdToId } from "../../utils/record-id-to-id";

type ResidentType = "holding" | "lease" | "sublet";

type OverviewRow = Apartment & {
  residentType: ResidentType;
  residentName: string;
  fromDate: Date;
  toDate?: Date;
  email?: string;
  phone?: string;
  identificationNumber?: string;
  age?: number;
  ownershipShare?: number;
  holdingId?: string;
  leaseId?: string;
  subletId?: string;
};

export function Overview() {
  const { locale: t, get: getLocale } = useLocale();
  const { data: apartments, isLoading: isApartmentsLoading } =
    useGetApartments();
  const [searchParams, setSearchParams] = useSearchParams();

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  const rows: OverviewRow[] = apartments ? flattenResidents(apartments) : [];

  return (
    <div className="w-full min-h-full overflow-x-auto">
      <div className="flex justify-between items-center">
        <H1>{getLocale("overview.overview")}</H1>
      </div>
      <Table
        actions={(row: OverviewRow) => {
          const apartmentFilterBy = encodeURIComponent(
            JSON.stringify([
              {
                field: "apartment.index",
                value: [row.index],
                operator: "eq",
              },
              { field: "active", value: [true], operator: "eq" },
            ]),
          );

          return [
            {
              label: getLocale("overview.viewApartment"),
              route: `/admin/register/apartment/${recordIdToId(row.id)}`,
            },
            ...(row.holdingId
              ? [
                  {
                    label: getLocale("overview.viewHolding"),
                    route: `/admin/register/holding/?filterBy=${apartmentFilterBy}`,
                  },
                ]
              : []),
            ...(row.leaseId
              ? [
                  {
                    label: getLocale("overview.viewLease"),
                    route: `/admin/register/lease/?filterBy=${apartmentFilterBy}`,
                  },
                ]
              : []),
            ...(row.subletId
              ? [
                  {
                    label: getLocale("overview.viewSublet"),
                    route: `/admin/register/sublet/?filterBy=${apartmentFilterBy}`,
                  },
                ]
              : []),
          ];
        }}
        data={rows}
        filterBy={
          JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
        }
        loading={isApartmentsLoading}
        locale={{ yes: getLocale("yes"), no: getLocale("no"), actions: getLocale("action.actions"), empty: getLocale("empty") }}
        selectedColumns={
          JSON.parse(
            searchParams.get("selectedColumns") || "[]",
          ) as string[]
        }
        showFieldSelector
        sortBy={searchParams.get("sortBy") || "index"}
        sortByDirection={
          searchParams.get("sortByDirection") as "asc" | "desc"
        }
        onFieldSelectorChange={(value) => {
          updateSearchParams({
            selectedColumns: JSON.stringify(value),
          });
        }}
        onFilter={(value: Filter[]) => {
          updateSearchParams({
            filterBy: JSON.stringify(value),
          });
        }}
        onSort={(field, direction) => {
          updateSearchParams({
            sortBy: field,
            sortByDirection: direction,
          });
        }}
      >
        <Column alwaysShow field="index">
          #
        </Column>
        <Column field="objectNumber">
          {getLocale("apartment.objectNumber")}
        </Column>
        <Column field="streetNumber" filterableBy="select" filterOptions={(rows: OverviewRow[]) => rows.filter((row, index, self) => self.findIndex(r => r.streetNumber === row.streetNumber) === index).map(row => ({
          label: row.streetNumber,
          value: row.streetNumber,
        }))}>
          {getLocale("apartment.streetNumber")}
        </Column>
        <Column field="apartmentNumber">
          {getLocale("apartment.apartmentNumber")}
        </Column>
        <Column field="floorNumber">
          {getLocale("apartment.floorNumber")}
        </Column>
        <Column
          body={(row: OverviewRow) =>
            row.area !== undefined ? `${row.area}m²` : "-"
          }
          field="area"
        >
          {getLocale("apartment.area")}
        </Column>
        <Column
          body={(row: OverviewRow) =>
            row.share !== undefined ? formatPercentage(row.share, 4) : "-"
          }
          field="share"
        >
          {getLocale("apartment.share")}
        </Column>
        <Column
          alwaysShow
          body={(row: OverviewRow) => row.residentName}
          field="residentName"
          filterableBy="text"
        >
          {getLocale("overview.resident")}
        </Column>
        <Column
          alwaysShow
          body={(row: OverviewRow) => {
            const typeLabels: Record<ResidentType, string> = {
              holding: getLocale("overview.residentTypeHolding"),
              lease: getLocale("overview.residentTypeLease"),
              sublet: getLocale("overview.residentTypeSublet"),
            };
            return typeLabels[row.residentType];
          }}
          field="residentType"
          filterableBy="select"
          filterOptions={() => [
            {
              label: getLocale("overview.residentTypeHolding"),
              value: "holding",
            },
            {
              label: getLocale("overview.residentTypeLease"),
              value: "lease",
            },
            {
              label: getLocale("overview.residentTypeSublet"),
              value: "sublet",
            },
          ]}
        >
          {getLocale("overview.residentType")}
        </Column>
        <Column
          alwaysShow
          body={(row: OverviewRow) => formatDate(row.fromDate)}
          field="fromDate"
          filterableBy="date"
        >
          {getLocale("fromDate")}
        </Column>
        <Column
          body={(row: OverviewRow) =>
            row.toDate ? formatDate(row.toDate) : "-"
          }
          field="toDate"
          filterableBy="date"
        >
          {getLocale("toDate")}
        </Column>
        <Column
          body={(row: OverviewRow) => row.email ?? "-"}
          field="email"
          filterableBy="text"
        >
          {getLocale("overview.email")}
        </Column>
        <Column
          body={(row: OverviewRow) => row.phone ?? "-"}
          field="phone"
          filterableBy="text"
        >
          {getLocale("overview.phone")}
        </Column>
        <Column
          body={(row: OverviewRow) => row.identificationNumber ?? "-"}
          field="identificationNumber"
          filterableBy="text"
        >
          {getLocale("overview.identificationNumber")}
        </Column>
        <Column
          body={(row: OverviewRow) => row.age ?? "-"}
          field="age"
        >
          {getLocale("overview.age")}
        </Column>
        <Column
          body={(row: OverviewRow) =>
            formatContractLength(row.fromDate, row.toDate, {
              year: t.year,
              years: t.years,
              month: t.month,
              months: t.months,
            })
          }
          field="contractLength"
          sortCallback={(
            a: OverviewRow,
            b: OverviewRow,
            _field: string,
            direction: "asc" | "desc",
          ) => compareContractLength(a, b, direction)}
        >
          {getLocale("overview.contractLength")}
        </Column>
        <Column
          body={(row: OverviewRow) =>
            row.ownershipShare !== undefined
              ? formatPercentage(row.ownershipShare, 2)
              : "-"
          }
          field="ownershipShare"
        >
          {getLocale("overview.ownershipShare")}
        </Column>
      </Table>
    </div>
  );
}

function flattenResidents(
  apartments: ApartmentWithHoldingsAndLeasesAndSublets[],
): OverviewRow[] {
  const now = new Date();
  const rows: OverviewRow[] = [];

  for (const apartment of apartments) {
    const currentHolding = getResidencyWithinDate(apartment.holdings, now);
    const currentLease = getResidencyWithinDate(apartment.leases, now);

    if (currentHolding) {
      const currentSublet = getResidencyWithinDate(currentHolding.sublets, now);

      for (const owner of currentHolding.owners) {
        rows.push(
          buildRow(apartment, "holding", residentNameFromOwner(owner), currentHolding.fromDate, currentHolding.toDate, {
            holdingId: currentHolding.id,
            ownershipShare: owner.share,
            ...contactInfoFromOwner(owner),
          }),
        );
      }

      if (currentSublet) {
        for (const tenant of currentSublet.tenants) {
          rows.push(
            buildRow(apartment, "sublet", residentNameFromTenant(tenant), currentSublet.fromDate, currentSublet.toDate, {
              holdingId: currentHolding.id,
              subletId: currentSublet.id,
              ...contactInfoFromTenant(tenant),
            }),
          );
        }
      }
    }

    if (currentLease) {
      const currentSublet = getResidencyWithinDate(currentLease.sublets, now);

      for (const tenant of currentLease.tenants) {
        rows.push(
          buildRow(apartment, "lease", residentNameFromTenant(tenant), currentLease.fromDate, currentLease.toDate, {
            leaseId: currentLease.id,
            ...contactInfoFromTenant(tenant),
          }),
        );
      }

      if (currentSublet) {
        for (const tenant of currentSublet.tenants) {
          rows.push(
            buildRow(apartment, "sublet", residentNameFromTenant(tenant), currentSublet.fromDate, currentSublet.toDate, {
              leaseId: currentLease.id,
              subletId: currentSublet.id,
              ...contactInfoFromTenant(tenant),
            }),
          );
        }
      }
    }
  }

  return rows;
}

function buildRow(
  apartment: ApartmentWithHoldingsAndLeasesAndSublets,
  residentType: ResidentType,
  residentName: string,
  fromDate: Date,
  toDate: Date | undefined,
  extra: {
    holdingId?: string;
    leaseId?: string;
    subletId?: string;
    ownershipShare?: number;
    email?: string;
    phone?: string;
    identificationNumber?: string;
    age?: number;
  },
): OverviewRow {
  return {
    ...apartment,
    residentType,
    residentName,
    fromDate,
    toDate,
    ...extra,
  };
}

function residentNameFromOwner(owner: HoldingOwner): string {
  if ("person" in owner) {
    return [owner.person.firstName, owner.person.lastName]
      .filter(Boolean)
      .join(" ");
  }
  return owner.organization.name ?? "";
}

function residentNameFromTenant(tenant: Tenant): string {
  if ("person" in tenant) {
    return [tenant.person.firstName, tenant.person.lastName]
      .filter(Boolean)
      .join(" ");
  }
  return tenant.organization.name ?? "";
}

type ContactInfo = {
  email?: string;
  phone?: string;
  identificationNumber?: string;
  age?: number;
};

function contactInfoFromOwner(owner: HoldingOwner): ContactInfo {
  if ("person" in owner) {
    return {
      email: owner.person.email,
      phone: owner.person.phone,
      identificationNumber: owner.person.personalIdentificationNumber,
      age: getAgeFromPersonalIdentificationNumber(
        owner.person.personalIdentificationNumber,
      ),
    };
  }
  return {
    email: owner.organization.email,
    phone: owner.organization.phone,
    identificationNumber: owner.organization.corporateRegistrationNumber,
  };
}

function contactInfoFromTenant(tenant: Tenant): ContactInfo {
  if ("person" in tenant) {
    return {
      email: tenant.person.email,
      phone: tenant.person.phone,
      identificationNumber: tenant.person.personalIdentificationNumber,
      age: getAgeFromPersonalIdentificationNumber(
        tenant.person.personalIdentificationNumber,
      ),
    };
  }
  return {
    email: tenant.organization.email,
    phone: tenant.organization.phone,
    identificationNumber: tenant.organization.corporateRegistrationNumber,
  };
}
